import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { addDays, subDays } from 'date-fns';
import { useCallback, useContext, useEffect, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { toast } from 'react-toastify';
import { Button, InputDateMask, InputRadio, InputSelectNative, InputText, P, RELATIONSHIPS, formatISODateString, reportAction, scrollToTop, AnalyticsContext, } from 'shared';
import ShowTerms from './ShowTerms';
const maxDays = 9000; // 24 years
const minDays = 40000; // 109.5 Years
// Date Constants
const TODAY = subDays(new Date(), minDays);
const FROM_TODAY_MAX = addDays(new Date(), maxDays); // 24 years
const DATE_NOW = {
    month: TODAY.getMonth(),
    day: TODAY.getDate(),
    year: TODAY.getFullYear(),
};
const DATE_MAX = {
    month: FROM_TODAY_MAX.getMonth(),
    day: FROM_TODAY_MAX.getDate(),
    year: FROM_TODAY_MAX.getFullYear(),
};
function AuthBuyerForm({ submitFunc, onSubmitSuccess, applicationEnvironment, terms, submitButtonText, showTerms, waterFalling, prequalification, }) {
    const linkCCA = terms.filter((itm) => itm.text.includes('cca'));
    const linkPatAct = terms.filter((itm) => itm.text.includes('patriot'));
    const initVals = {
        shareWithFamily: 'N',
    };
    const [isAddAuthBuyer, setIsAddAuthBuyer] = useState(null);
    const { analytics } = useContext(AnalyticsContext);
    const handleRadioShareWithFamily = (evt) => {
        const tmpVal = evt.target.value;
        if (tmpVal === 'Y') {
            setIsAddAuthBuyer(true);
        }
        if (tmpVal === 'N') {
            setIsAddAuthBuyer(false);
        }
    };
    const formValidate = (values) => {
        if (values) {
            const errors = {};
            if (values.shareWithFamily === 'Y') {
                if (!values.firstName) {
                    errors.firstName = 'Required';
                }
                if (!values.lastName) {
                    errors.lastName = 'Required';
                }
                if (!values.relationship) {
                    errors.relationship = 'Required';
                }
                if (!values.dateOfBirth) {
                    errors.dateOfBirth = 'Required';
                }
            }
            if (values.shareWithFamily === 'N') {
                if (!values.signedAgreed) {
                    errors.signedAgreed = 'Required';
                }
            }
            if (Object.keys(errors).length) {
                return errors;
            }
            else {
                return undefined;
            }
        }
    };
    const createSubmitObject = (values) => {
        if (values.shareWithFamily === 'Y') {
            const fmtDOB = formatISODateString(values.dateOfBirth);
            return {
                authorizedBuyer: {
                    firstName: values.firstName,
                    lastName: values.lastName,
                    relationship: values.relationship,
                    dateOfBirth: fmtDOB,
                },
                shareWithFamily: values.shareWithFamily,
                signedAgreed: values.signedAgreed,
            };
        }
        else {
            return {
                shareWithFamily: values.shareWithFamily,
                signedAgreed: values.signedAgreed,
            };
        }
    };
    const formSubmit = useCallback(async (values) => {
        if (values) {
            const isFormValid = formValidate(values);
            if (isFormValid === undefined) {
                const submitObj = createSubmitObject(values);
                if (submitFunc) {
                    const resp = await submitFunc(submitObj);
                    onSubmitSuccess(resp);
                }
            }
            else {
                toast(`Form has errors: ${JSON.stringify(isFormValid)}`, {
                    type: 'error',
                });
                // Form IS NOT VALID
                return isFormValid;
            }
        }
    }, [isAddAuthBuyer]);
    const logToAnalytics = () => {
        if (prequalification.offers && prequalification.offers.length > 1) {
            analytics?.track('ViewMoreOffersButtonShown', {
                product: 'Alphaeon Credit',
            });
            reportAction('ViewMoreOffersButtonShown', {
                componentName: 'AuthBuyerForm',
                actionType: 'ViewMoreOffersButtonShown',
                actionURL: window.location.pathname,
                actionData: {
                    product: 'Alphaeon Credit',
                },
            });
        }
        analytics?.track('OfferPresentedBread', {});
        reportAction('OfferPresentedBread', {
            componentName: 'AuthBuyerForm',
            actionType: 'OfferPresentedBread',
            actionURL: window.location.pathname,
        });
    };
    useEffect(() => {
        scrollToTop();
        logToAnalytics();
    }, []);
    // Default
    return (_jsx("div", { children: _jsx(Form, { initialValues: { ...initVals }, onSubmit: formSubmit, render: ({ handleSubmit, submitting, values, form }) => {
                const signedAgreed = values.signedAgreed;
                const yesAuthBuyer = values.shareWithFamily === 'Y' ? true : false;
                const isDisabled = submitting ||
                    (!yesAuthBuyer && !signedAgreed) ||
                    (yesAuthBuyer &&
                        (!values.firstName ||
                            !values.lastName ||
                            !values.relationship ||
                            !values.dateOfBirth ||
                            !signedAgreed));
                // Reset if not adding an auth buyer
                if (!isAddAuthBuyer) {
                    form.change('firstName', undefined);
                    form.change('lastName', undefined);
                    form.change('relationship', undefined);
                    form.change('dateOfBirth', undefined);
                }
                return (_jsxs("form", { noValidate: true, onSubmit: handleSubmit, className: `forms-container`, children: [applicationEnvironment?.authorizedBuyersEnabled === true && (_jsxs("div", { className: "preQualification-form radio-group", children: [_jsx(P, { className: "info-contain", variant: "title-large", bold: true, children: "Want to Add an Authorized Buyer?" }), _jsxs("fieldset", { onChange: handleRadioShareWithFamily, children: [_jsx(Field, { name: "shareWithFamily", label: false, component: InputRadio, type: "radio", value: "Y", radiotext: "Yes" }), _jsx(Field, { name: "shareWithFamily", label: false, component: InputRadio, type: "radio", value: "N", radiotext: "No" })] }), isAddAuthBuyer ? (_jsxs("div", { children: [_jsx(Field, { name: "firstName", label: 'First Name', component: InputText, placeholder: "", type: "text", required: yesAuthBuyer ? true : false }), _jsx(Field, { name: "lastName", label: 'Last Name', component: InputText, placeholder: "", type: "text", required: yesAuthBuyer ? true : false }), _jsx(InputSelectNative, { name: "relationship", label: 'Relationship To Applicant', options: RELATIONSHIPS, required: yesAuthBuyer ? true : false }), _jsx(Field, { placeholder: 'MM/DD/YYYY', name: 'dateOfBirth', label: 'Date of Birth', component: InputDateMask, required: yesAuthBuyer ? true : false, type: "tel", mask: Date, autofix: "pad", min: new Date(DATE_NOW.year, DATE_NOW.month, DATE_NOW.day), max: new Date(DATE_MAX.year, DATE_MAX.month, DATE_MAX.day), lazy: true, unmask: false }), _jsx(P, { className: "authorized-buyer-text", children: "An authorized buyer is an individual you give permission to make purchases on your account. As the primary account holder, you are responsible for paying for all purchases made on this account, including any made by an authorized buyer." })] })) : null] })), _jsx(ShowTerms, { showTerms: showTerms, linkPatAct: linkPatAct, linkCCA: linkCCA }), _jsx("div", { children: _jsx(Button, { type: "submit", "data-testid": "authbuyer-form-submit-button", className: `button-primary button-warn ${isDisabled ? 'is-disabled' : 'is-enabled'} ${submitting || submitting ? 'in-progress' : ''}`, invalid: isDisabled || waterFalling, submitting: waterFalling, disabledToolTip: "Please fill out all required fields and correct any validation errors.", children: submitButtonText }) })] }));
            } }) }));
}
export default AuthBuyerForm;
